import type { DepthConfidence } from "@/lib/conditions/snow-depth-validation";
import type { ConfidenceClass } from "@/lib/conditions/ski/types";
import {
  confidenceClassWord,
  flagChipLabels,
  likelyRangeText,
} from "@/app/_components/conditions/ski/ski-v3-words";

type Props = {
  confidence: DepthConfidence;
  reason?: string | null;
  lo?: number | null;
  hi?: number | null;
  /**
   * Ski v3 evidence class. When present the badge shows the class in plain words and the
   * P10 to P90 range as "Likely 62 to 78" (for every class, including A). Absent on
   * revision 2 rows, which keep the legacy badge below.
   */
  cls?: ConfidenceClass | null;
  /** Ski v3 flags (cover-risk, ice-risk, cover-assumed); shown as small chips beside the badge. */
  flags?: readonly string[] | null;
};

const REASON_COPY: Record<string, string> = {
  "anchor-stale": "The latest depth anchor is outside its measured range.",
  "depth-gate-uncertain": "The evidence cannot confirm that there is enough snow.",
  "depth-spread-too-wide": "The measured score range is too wide to publish.",
  "missing-provenance": "No verified snow-depth evidence is attached to this score.",
  "no-error-model": "This depth source has no measured error model.",
  "validation-failed": "The available snow-depth evidence did not pass validation.",
  "seasonal-snow-estimate": "Seasonal estimate: current snow cover is unverified. The range describes plausible conditions, not a calibrated confidence interval.",
  "evolved-snow-estimate": "Snow cover is estimated from earlier evidence and weather. Current depth is uncertain.",
  "snow-cover-uncertain": "Snow cover is estimated across plausible snow states. The range is a scenario envelope, not a measured confidence interval.",
};

/** Plain explanations of the classes (contract Section 8.3). */
export const CLASS_EXPLANATION: Record<ConfidenceClass, string> = {
  A: "Recent reports from the resort, a nearby station or other skiers back this score.",
  B: "Some evidence backs this score, such as resort reports from the last few days, lift status, a station within 30 km or a recent satellite pass.",
  C: "Estimated from weather models, with little recent evidence to check it.",
};

const CLASS_STYLE: Record<ConfidenceClass, string> = {
  A: "border-emerald-300 bg-emerald-50 text-emerald-900",
  B: "border-amber-300 bg-amber-50 text-amber-900",
  C: "border-dashed border-slate-400 bg-slate-50 text-slate-700",
};

export function scoreConfidenceReason(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return REASON_COPY[reason] ?? reason;
}

export function scoreReadingWithConfidenceRange(
  score: number | string,
  lo: number | null | undefined,
  hi: number | null | undefined,
): string {
  return Number.isInteger(lo) && Number.isInteger(hi)
    ? `${score} (${lo}-${hi})`
    : String(score);
}

/**
 * The v3 class badge. The explanation sits in a disclosure (not only a `title`), so touch and
 * keyboard users can read it; the summary is at least 44 px tall. `explanation` replaces the ski
 * wording for other sports (SportConfidenceBadge.tsx).
 */
export function ClassBadge({ cls, lo, hi, flags, explanation }: { cls: ConfidenceClass; lo?: number | null; hi?: number | null; flags?: readonly string[] | null; explanation?: string }) {
  const word = confidenceClassWord(cls) ?? cls;
  const range = likelyRangeText(lo, hi);
  const chips = flagChipLabels(flags);
  const accessibleLabel = [`Confidence: ${word}`, range].filter(Boolean).join(". ");
  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1.5" data-confidence-class={cls}>
      <details className="inline-block max-w-full align-middle">
        <summary
          aria-label={accessibleLabel}
          className="inline-flex min-h-11 max-w-full cursor-pointer list-none items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden"
        >
          <span
            className={`inline-flex min-h-6 max-w-full flex-wrap items-center gap-x-1.5 rounded-md border px-2 py-1 text-[10px] font-semibold leading-[1.25] ${CLASS_STYLE[cls]}`}
          >
            <span>{word}</span>
            {range && <span className="font-medium opacity-80">· {range}</span>}
            <span aria-hidden="true" className="opacity-70">ⓘ</span>
          </span>
        </summary>
        <p className="max-w-[18rem] pb-1 text-[11.5px] font-normal leading-[1.5] text-text-body" data-confidence-explanation="">
          {explanation ?? CLASS_EXPLANATION[cls]}
        </p>
      </details>
      {chips.map((chip) => (
        <span
          key={chip}
          className="inline-flex min-h-6 items-center rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-amber-900"
        >
          {chip}
        </span>
      ))}
    </span>
  );
}

export function ScoreConfidenceBadge({ confidence, reason, lo, hi, cls, flags }: Props) {
  if (cls === "A" || cls === "B" || cls === "C") return <ClassBadge cls={cls} lo={lo} hi={hi} flags={flags} />;
  if (confidence === "full") return null;

  const range = Number.isInteger(lo) && Number.isInteger(hi)
    ? `${lo}-${hi}`
    : null;
  const explanation = scoreConfidenceReason(reason);
  const label = confidence === "partial"
    ? "Estimated"
    : confidence === "inferred"
      ? reason === "seasonal-snow-estimate" ? "Seasonal estimate" : "Snow cover uncertain"
      : "Snow depth unavailable";
  const detail = confidence === "unavailable" ? explanation : range;
  const accessibleLabel = [label, detail].filter(Boolean).join(". ");

  // A plain span cannot carry an aria-label (screen readers ignore it), so the accessible
  // wording is visually hidden text and the compact visible text is hidden from them.
  return (
    <span
      data-score-confidence={confidence}
      className={`inline-flex min-h-6 max-w-full flex-wrap items-center gap-x-1.5 rounded-md border px-2 py-1 text-[10px] font-semibold leading-[1.25] ${
        confidence === "partial"
          ? "border-amber-300 bg-amber-50 text-amber-900"
          : confidence === "inferred"
            ? "border-dashed border-slate-400 bg-slate-50 text-slate-700"
            : "border-rose-300 bg-rose-50 text-rose-800"
      }`}
      title={explanation ?? accessibleLabel}
    >
      <span aria-hidden="true">{label}</span>
      {detail && (
        <span aria-hidden="true" className="font-medium opacity-80">
          · {detail}
        </span>
      )}
      <span className="sr-only">{accessibleLabel}</span>
    </span>
  );
}
